"use client";

import React from "react";
import { AlertTriangle } from "lucide-react";

interface DeleteConfirmationModalProps {
  header?: string;
  title?: string;
  message?: string;
  extraMessage?: string; // Additional message content
  entity?: string; // e.g., "view", "note", "property"
  isOpen: boolean;
  isDeleting?: boolean;
  isProcessing?: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: "red" | "blue" | "gray";
  count?: number;
  singleTitle?: string;
  // Optional single-select option group rendered between the message and the
  // action buttons. Nothing extra renders when `options` is absent or empty.
  options?: Array<{ value: string; label: string; description?: string }>;
  selectedOption?: string;
  onOptionChange?: (value: string) => void;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  header,
  title,
  message,
  extraMessage,
  entity = "item",
  isOpen,
  onCancel,
  onConfirm,
  isDeleting = false,
  isProcessing = false,
  confirmButtonText,
  cancelButtonText = "Cancel",
  confirmButtonColor = "red",
  count,
  singleTitle,
  options,
  selectedOption,
  onOptionChange,
}) => {
  if (!isOpen) return null;

  const processing = isDeleting || isProcessing;
  const formattedTitle = title ? title.charAt(0).toUpperCase() + title.slice(1) : title;
  const formattedSingleTitle = singleTitle
    ? singleTitle.charAt(0).toUpperCase() + singleTitle.slice(1)
    : singleTitle;

  const isGroup = count !== undefined && count > 1;
  const isSingle = count === 1;

  const optionList = options ?? [];
  const hasOptions = optionList.length > 0;
  const hasSelection = optionList.some((option) => option.value === selectedOption);

  const getConfirmText = () => {
    if (processing && confirmButtonText) {
      // If processing and custom text provided, append "..." if not already present
      return confirmButtonText.endsWith("...") ? confirmButtonText : `${confirmButtonText}...`;
    }
    if (confirmButtonText) return confirmButtonText;
    if (processing) {
      if (confirmButtonColor === "red") return "Deleting...";
      return "Processing...";
    }
    if (confirmButtonColor === "red") return "Delete";
    return "Confirm";
  };

  const getMessage = () => {
    let mainMessage = message;

    if (!mainMessage) {
      if (isGroup) {
        mainMessage = `Are you sure you want to delete these ${entity} (${count} selected)?`;
      } else if (isSingle && singleTitle) {
        mainMessage = `Are you sure you want to delete this ${entity} titled ${formattedSingleTitle}?`;
      } else if (title) {
        mainMessage = `Are you sure you want to delete this ${entity} titled ${formattedTitle}?`;
      } else {
        mainMessage = `Are you sure you want to delete this ${entity}?`;
      }
    }

    if (extraMessage) {
      return (
        <>
          {mainMessage}
          <br />
          <br />
          <div className="flex items-start gap-2 rounded-md bg-amber-500/15 px-3 py-2.5 text-sm text-amber-600 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{extraMessage}</span>
          </div>
        </>
      );
    }

    return mainMessage;
  };

  const getModalHeader = () => {
    if (header) return header;
    if (title) return `Delete ${title}`;
    if (confirmButtonColor === "red") return "Confirm Deletion";
    return "Confirm Action";
  };

  const getConfirmButtonClass = () => {
    const base = "px-4 py-2 text-sm rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed";
    switch (confirmButtonColor) {
      case "blue":
        return `${base} bg-blue-500 text-white hover:bg-blue-700`;
      case "gray":
        return `${base} bg-gray-600 text-white hover-component`;
      case "red":
      default:
        return `${base} bg-red-600 text-white hover:bg-red-700`;
    }
  };

  // Accent for the selected option card — follows the confirm button's color so
  // the group reads as part of the same (destructive by default) action.
  const getOptionAccent = () => {
    switch (confirmButtonColor) {
      case "blue":
        return { border: "border-blue-500", fill: "bg-blue-500", tint: "bg-blue-500/10", ring: "focus-visible:ring-blue-500/60" };
      case "gray":
        return { border: "border-gray-500", fill: "bg-gray-500", tint: "bg-gray-500/10", ring: "focus-visible:ring-gray-500/60" };
      case "red":
      default:
        return { border: "border-red-500", fill: "bg-red-500", tint: "bg-red-500/10", ring: "focus-visible:ring-red-500/60" };
    }
  };

  const optionAccent = getOptionAccent();

  // Arrow keys move (and select) within the group; Enter/Space are handled
  // natively by the underlying <button>.
  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (processing) return;
    const forward = event.key === "ArrowDown" || event.key === "ArrowRight";
    const backward = event.key === "ArrowUp" || event.key === "ArrowLeft";
    if (!forward && !backward) return;
    event.preventDefault();
    const nextIndex = (index + (forward ? 1 : -1) + optionList.length) % optionList.length;
    const nextOption = optionList[nextIndex];
    if (!nextOption) return;
    onOptionChange?.(nextOption.value);
    const radios = event.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[role="radio"]');
    radios?.[nextIndex]?.focus();
  };

  return (
    // Bottom-anchored on mobile (bottom sheet) so it slides up from
    // the bottom and sits in the thumb zone; centered on desktop (current
    // behavior). `items-end md:items-center` is the single switch.
    <div className="fixed inset-0 z-[12000] bg-black/50 dark:bg-black/70 flex items-end md:items-center justify-center">
      {/* Mobile: full width, top corners only, max 90dvh + scroll if needed.
          Desktop: original fixed 400px width, all corners rounded. */}
      <div className="bg-modal border border-component p-6 shadow-lg w-full md:w-[400px] md:max-w-full rounded-t-lg md:rounded-lg max-h-[90dvh] md:max-h-none overflow-y-auto md:overflow-visible">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white !m-0 break-words">
          {getModalHeader()}
        </h2>
        <div className={` mt-2 text-sm text-gray-700 dark:text-gray-300 break-words ${hasOptions ? "mb-3" : "mb-6"}`}>
          {getMessage()}
        </div>
        {hasOptions && (
          <div role="radiogroup" aria-label={getModalHeader()} className="flex flex-col gap-2 mb-6">
            {optionList.map((option, index) => {
              const isSelected = option.value === selectedOption;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  disabled={processing}
                  tabIndex={isSelected || (!hasSelection && index === 0) ? 0 : -1}
                  onClick={() => onOptionChange?.(option.value)}
                  onKeyDown={(event) => handleOptionKeyDown(event, index)}
                  className={`w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-md border transition-colors focus:outline-none focus-visible:ring-2 ${optionAccent.ring} disabled:opacity-50 disabled:cursor-not-allowed ${
                    isSelected
                      ? `${optionAccent.border} ${optionAccent.tint}`
                      : "border-component hover-component"
                  }`}
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="block mt-0.5 text-xs text-sec">{option.description}</span>
                    )}
                  </span>
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border flex items-center justify-center ${
                      isSelected ? optionAccent.border : "border-component"
                    }`}
                  >
                    {isSelected && <span className={`h-2 w-2 rounded-full ${optionAccent.fill}`} />}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 text-sm bg-modal border border-component text-gray-800 dark:text-white rounded-md hover-component disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={onCancel}
            disabled={processing}
            type="button"
          >
            {cancelButtonText}
          </button>
          <button
            className={getConfirmButtonClass()}
            onClick={onConfirm}
            disabled={processing}
            type="button"
          >
            {getConfirmText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;


